import React, { useEffect, useState } from 'react';

const DIR_FULL = {N:'North',S:'South',E:'East',W:'West'};
const statusColor = s => s==='COMPLETED'?'#3B6D11':s==='ON_HOLD'?'#854F0B':'#185FA5';
const statusBg    = s => s==='COMPLETED'?'#EAF3DE':s==='ON_HOLD'?'#FAEEDA':'#E6F1FB';
const formatDate  = d => { try{ return new Date(d).toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long',year:'numeric'}); }catch{ return d; }};

const Row = ({label, val, mono, bold, color}) => (
  <div style={{display:'flex',justifyContent:'space-between',padding:'2px 0',borderBottom:'0.5px solid #f0f0f0'}}>
    <span style={{fontSize:12,color:'#666',minWidth:130}}>{label}</span>
    <span style={{fontSize:12,fontFamily:mono?'monospace':'inherit',fontWeight:bold?700:500,color:color||'#1e3a5f'}}>{val}</span>
  </div>
);

const SectionTitle = ({children}) => (
  <div style={{fontSize:10,fontWeight:700,color:'#1e3a5f',textTransform:'uppercase',letterSpacing:'1px',borderBottom:'1px solid #1e3a5f',paddingBottom:2,marginBottom:4,marginTop:8}}>{children}</div>
);

const WorkEntry = ({entry, idx}) => {
  const d = entry.data || {};
  const wt = entry.work_type;
  const wtLabel = {DRILLING:'Drilling',WIRE_SAW:'Wire Saw',BLASTING:'Blasting',OFFSET:'Offset Work',WASTE:'Waste Management',TRANSPORT:'Transport',OTHER:'Other Work'};
  const headerColor = wt==='DRILLING'?'#1e3a5f':wt==='WIRE_SAW'?'#185FA5':wt==='BLASTING'?'#C0392B':wt==='TRANSPORT'?'#854F0B':'#555';

  const renderDrilling = () => (
    <div>
      {(d.selected_machines||[]).map((machine,mi)=>{
        const md=(d.machine_data||{})[machine]||{};
        const totalFt=(md.holes||[]).reduce((s,h)=>s+(parseFloat(h.running_feet)||0),0);
        const hrs=((parseFloat(md.running_hours||0)*60)+parseFloat(md.running_minutes||0))/60;
        return (
          <div key={mi} style={{marginBottom:8,padding:8,background:'#f8f9fc',borderRadius:4,border:'1px solid #e8eaf0'}}>
            <div style={{fontSize:13,fontWeight:700,color:'#1e3a5f',marginBottom:6,display:'flex',justifyContent:'space-between'}}>
              <span>🔩 {machine}</span>
              {hrs>0&&totalFt>0&&<span style={{fontSize:12,background:'#1e3a5f',color:'#fff',padding:'1px 8px',borderRadius:20}}>{(totalFt/hrs).toFixed(2)} ft/hr</span>}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
              <div>
                <SectionTitle>Machine</SectionTitle>
                {md.running_hours&&<Row label="Running Hours" val={`${md.running_hours}h ${md.running_minutes||0}m`} mono/>}
                {md.operator?.operator_name&&<Row label="Operator" val={md.operator.operator_name} bold/>}
                {md.compressor?.fuel&&<Row label="Fuel" val={`${md.compressor.fuel} L`} mono color="#854F0B"/>}
              </div>
              <div>
                <SectionTitle>Drill Bit</SectionTitle>
                {md.drill_bit_given&&<>
                  <Row label="Given" val={`${md.drill_bit_given}"`} mono/>
                  <Row label="Taken Back" val={`${md.drill_bit_taken||0}"`} mono/>
                  <Row label="Net Used" val={`${(parseFloat(md.drill_bit_given||0)-parseFloat(md.drill_bit_taken||0)).toFixed(3)}"`} mono bold color="#185FA5"/>
                </>}
              </div>
              <div>
                <SectionTitle>Holes — {totalFt.toFixed(2)} ft</SectionTitle>
                <div style={{display:'flex',flexWrap:'wrap',gap:3}}>
                  {(md.holes||[]).map((h,hi)=>(
                    <div key={hi} style={{background:'#E6F1FB',border:'1px solid #B5D4F4',borderRadius:3,padding:'1px 6px',fontSize:11}}>
                      {h.hole}: <strong>{h.running_feet}ft</strong>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderWireSaw = () => (
    <div>
      {(d.selected_machines||[]).map((machine,mi)=>{
        const md=(d.machine_data||{})[machine]||{};
        const hrs=((parseFloat(md.running_hours||0)*60)+parseFloat(md.running_minutes||0))/60;
        const area=parseFloat(md.cutting_area||0);
        return (
          <div key={mi} style={{padding:8,background:'#f8f9fc',borderRadius:4,border:'1px solid #e8eaf0'}}>
            <div style={{fontSize:13,fontWeight:700,color:'#185FA5',marginBottom:6,display:'flex',justifyContent:'space-between'}}>
              <span>🔪 {machine} — {d.cutting_type}</span>
              {hrs>0&&area>0&&<span style={{fontSize:12,background:'#185FA5',color:'#fff',padding:'1px 8px',borderRadius:20}}>{(area/hrs).toFixed(2)} Sq.m/hr</span>}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
              <div>
                <SectionTitle>Work</SectionTitle>
                {md.cutting_area&&<Row label="Cutting Area" val={`${md.cutting_area} Sq.m`} mono bold color="#185FA5"/>}
                {md.rope_consumption&&<Row label="Rope Used" val={`${md.rope_consumption} m`} mono/>}
              </div>
              <div>
                <SectionTitle>Power — {md.power_source}</SectionTitle>
                {md.running_hours&&<Row label="Hours" val={`${md.running_hours}h ${md.running_minutes||0}m`} mono/>}
                {md.diesel_consumption&&<Row label="Diesel" val={`${md.diesel_consumption} L`} mono color="#854F0B"/>}
              </div>
              <div>
                <SectionTitle>Operator</SectionTitle>
                {md.operator?.operator_name&&<Row label="Name" val={md.operator.operator_name} bold/>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderTransport = () => {
    const totalLoad=(parseFloat(d.trips||0)*parseFloat(d.load_per_trip||0)).toFixed(2);
    const fuelPerTrip=d.trips>0?(parseFloat(d.fuel_consumption||0)/parseFloat(d.trips||1)).toFixed(2):0;
    return (
      <div style={{padding:8,background:'#f8f9fc',borderRadius:4,border:'1px solid #e8eaf0'}}>
        <div style={{fontSize:13,fontWeight:700,color:'#854F0B',marginBottom:6}}>🚛 {d.vehicle_type}</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
          <div>
            <SectionTitle>Transport</SectionTitle>
            {d.material_type&&<Row label="Material" val={d.material_type}/>}
            {d.trips&&<Row label="Trips" val={d.trips} mono bold/>}
            {d.load_per_trip&&<Row label="Load/Trip" val={`${d.load_per_trip} T`} mono/>}
            {totalLoad>0&&<Row label="Total Load" val={`${totalLoad} T`} mono bold color="#1e3a5f"/>}
          </div>
          <div>
            <SectionTitle>Fuel</SectionTitle>
            {d.fuel_consumption&&<Row label="Total" val={`${d.fuel_consumption} L`} mono bold color="#854F0B"/>}
            {fuelPerTrip>0&&<Row label="Per Trip" val={`${fuelPerTrip} L`} mono color="#854F0B"/>}
            <SectionTitle>Route</SectionTitle>
            {d.from_location&&<Row label="From" val={d.from_location} bold/>}
            {d.to_location&&<Row label="To" val={d.to_location} bold/>}
          </div>
          <div>
            <SectionTitle>Driver</SectionTitle>
            {d.driver_name&&<Row label="Name" val={d.driver_name} bold/>}
          </div>
        </div>
      </div>
    );
  };

  const renderWaste = () => (
    <div style={{padding:8,background:'#f8f9fc',borderRadius:4,border:'1px solid #e8eaf0'}}>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
        <div>
          <SectionTitle>Waste Details</SectionTitle>
          {d.waste_source&&<Row label="Waste Source" val={d.waste_source}/>}
          {d.reason&&<Row label="Reason" val={d.reason}/>}
          {d.disposed&&<Row label="Disposed" val={d.disposed}/>}
          {d.disposed_to&&<Row label="Disposed To" val={d.disposed_to}/>}
        </div>
        <div>
          <SectionTitle>Operator</SectionTitle>
          {d.operator?.operator_name&&<Row label="Name" val={d.operator.operator_name} bold/>}
          {d.operator?.operator_role&&<Row label="Role" val={d.operator.operator_role}/>}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{border:'1px solid #ddd',borderRadius:6,marginBottom:8,overflow:'hidden',pageBreakInside:'avoid'}}>
      <div style={{padding:'6px 10px',display:'flex',justifyContent:'space-between',alignItems:'center',background:headerColor}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:12,fontWeight:700,color:'rgba(255,255,255,0.7)',background:'rgba(255,255,255,0.15)',padding:'1px 6px',borderRadius:20}}>Entry {idx+1}</span>
          <span style={{fontSize:14,fontWeight:700,color:'#fff'}}>{wtLabel[wt]||wt}</span>
        </div>
        <span style={{fontSize:12,padding:'2px 8px',borderRadius:20,fontWeight:600,background:statusBg(entry.status||'IN_PROGRESS'),color:statusColor(entry.status||'IN_PROGRESS')}}>
          {entry.status==='COMPLETED'?'✓ Completed':entry.status==='ON_HOLD'?'⏸ On Hold':'● In Progress'}
        </span>
      </div>
      <div style={{padding:'8px 10px'}}>
        {wt==='DRILLING'&&renderDrilling()}
        {wt==='WIRE_SAW'&&renderWireSaw()}
        {wt==='TRANSPORT'&&renderTransport()}
        {wt==='WASTE'&&renderWaste()}
        {entry.remarks&&entry.remarks!=='None'&&(
          <div style={{marginTop:6,padding:'4px 8px',background:'#fffbeb',border:'1px solid #fde68a',borderRadius:4}}>
            <div style={{fontSize:10,color:'#854F0B',fontWeight:700,textTransform:'uppercase',marginBottom:2}}>📝 Remarks</div>
            <div style={{fontSize:12,color:'#1e3a5f'}}>{entry.remarks}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function PrintDailyReportPage() {
  const [data, setData] = useState(null);

  useEffect(()=>{
    const stored = sessionStorage.getItem('printReport');
    if(stored) {
      setData(JSON.parse(stored));
      setTimeout(()=>window.print(), 1000);
    }
  }, []);

  if(!data) return <div style={{padding:20,fontFamily:'Arial'}}>Loading report...</div>;

  const { sessions, filters, reportNumber } = data;

  const allEntries = [];
  sessions.forEach(s=>{
    try {
      const notes = JSON.parse(s.notes||'{}');
      (notes.day_entries||[]).forEach(e=>{
        if(e.work_type && (e.entry_date===filters.date || s.started_date===filters.date)) {
          allEntries.push(e);
        }
      });
    } catch {}
  });

  const completed  = allEntries.filter(e=>e.status==='COMPLETED').length;
  const inProgress = allEntries.filter(e=>e.status==='IN_PROGRESS'||!e.status).length;
  let totalDiesel = 0;
  allEntries.forEach(entry=>{
    const d=entry.data||{};
    if(entry.work_type==='DRILLING') Object.values(d.machine_data||{}).forEach(m=>{ totalDiesel+=parseFloat(m.compressor?.fuel||0); });
    if(entry.work_type==='WIRE_SAW') Object.values(d.machine_data||{}).forEach(m=>{ totalDiesel+=parseFloat(m.diesel_consumption||0); });
    if(entry.work_type==='TRANSPORT') totalDiesel+=parseFloat(d.fuel_consumption||0);
  });

  return (
    <div style={{fontFamily:'Inter,Arial,sans-serif',fontSize:13,color:'#333',padding:'10mm',maxWidth:'210mm',margin:'0 auto'}}>
      <style>{`
        @media print {
          @page { margin: 8mm; size: A4; }
          body { margin: 0; }
        }
        * { box-sizing: border-box; }
      `}</style>

      {/* Company Header */}
      <div style={{borderBottom:'3px solid #1e3a5f',paddingBottom:8,marginBottom:10,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <img src="/TycheAbodes-logo1.png" alt="Logo" style={{width:75,height:75,objectFit:'contain'}} onError={e=>e.target.style.display='none'}/>
          <div>
            <div style={{fontSize:17,fontWeight:800,color:'#1e3a5f',fontFamily:'Inter,sans-serif'}}>TYCHE ABODES PRIVATE LIMITED</div>
            <div style={{fontSize:12,color:'#555'}}>No. 401, Ramani's Frazer Square, Near Head Post Office,</div>
            <div style={{fontSize:12,color:'#555'}}>Goods Shed Road, Coimbatore - 641001.</div>
            <div style={{fontSize:12,color:'#7f1b1b'}}>📞 +91 94433 23246 &nbsp;|&nbsp; GSTIN: 33AAKCT5967G1ZY</div>
          </div>
        </div>
        <div style={{textAlign:'right'}}>
          <div style={{fontSize:16,fontWeight:800,color:'#1e3a5f',textTransform:'uppercase',letterSpacing:1}}>Daily Work Report</div>
          <div style={{fontSize:12,color:'#555',marginTop:3}}>Report No: <strong>{reportNumber}</strong></div>
          <div style={{fontSize:12,color:'#555'}}>Date: <strong>{formatDate(filters.date)}</strong></div>
          <div style={{fontSize:12,color:'#555'}}>Generated: <strong>{new Date().toLocaleDateString('en-IN')}</strong></div>
        </div>
      </div>

      {/* Summary */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',border:'1px solid #ddd',borderRadius:6,overflow:'hidden',marginBottom:10}}>
        {[
          ['Sessions',sessions.length,'#1e3a5f'],
          ['Work Entries',allEntries.length,'#185FA5'],
          ['Completed',completed,'#3B6D11'],
          ['In Progress',inProgress,'#854F0B'],
          ['Total Diesel',`${totalDiesel.toFixed(1)} L`,'#C0392B'],
        ].map(([l,v,c],i)=>(
          <div key={i} style={{padding:'6px 10px',textAlign:'center',background:i%2===0?'#f8f9fc':'#fff',borderRight:i<4?'1px solid #ddd':'none'}}>
            <div style={{fontSize:18,fontWeight:700,color:c,fontFamily:'monospace'}}>{v}</div>
            <div style={{fontSize:11,color:'#666',textTransform:'uppercase',letterSpacing:'0.5px',marginTop:2}}>{l}</div>
          </div>
        ))}
      </div>

      {/* Sessions */}
      {sessions.map((session,si)=>{
        let parsedNotes={};
        let dayEntries=[];
        try {
          parsedNotes=JSON.parse(session.notes||'{}');
          dayEntries=(parsedNotes.day_entries||[]).filter(e=>e.entry_date===filters.date||session.started_date===filters.date);
        } catch {}
        if(dayEntries.length===0&&session.started_date===filters.date) dayEntries=parsedNotes.day_entries||[];

        return (
          <div key={si} style={{marginBottom:12}}>
            {/* Session Header */}
            <div style={{background:'#1e3a5f',padding:'6px 12px',borderRadius:'6px 6px 0 0',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <span style={{color:'#fff',fontWeight:700,fontSize:15,fontFamily:'monospace'}}>{session.session_number}</span>
                <span style={{color:'rgba(255,255,255,0.6)',fontSize:12,marginLeft:12}}>{session.shift} Shift</span>
              </div>
              <span style={{fontSize:12,padding:'2px 10px',borderRadius:20,fontWeight:600,background:statusBg(session.status),color:statusColor(session.status)}}>
                {(session.status||'').replace('_',' ')}
              </span>
            </div>

            {/* Supervisor Box */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',border:'1px solid #ddd',borderTop:'none',marginBottom:6}}>
              {[
                ['Supervisor', session.supervisor_name||'S Sivakumar'],
                ['Contact', '+91 97913 38573'],
                ['Shift', session.shift||'—'],
                ['Direction', DIR_FULL[session.direction]||session.direction||'—'],
                ['Stage', session.production_stage||'—'],
                ['Block Ref', session.production_stage==='GENERAL'?'—':session.block_reference||'—'],
              ].map(([l,v],i)=>(
                <div key={i} style={{padding:'4px 8px',borderRight:i%3<2?'1px solid #ddd':'none',borderBottom:i<3?'1px solid #ddd':'none',background:i%2===0?'#f8f9fc':'#fff'}}>
                  <div style={{fontSize:10,color:'#888',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:1}}>{l}</div>
                  <div style={{fontSize:12,fontWeight:600,color:'#1e3a5f'}}>{v}</div>
                </div>
              ))}
            </div>

            {/* Work Entries */}
            {dayEntries.filter(e=>e.work_type).map((entry,ei)=>(
              <WorkEntry key={ei} entry={entry} idx={ei}/>
            ))}
          </div>
        );
      })}

      {/* Footer */}
      <div style={{borderTop:'2px solid #1e3a5f',paddingTop:8,marginTop:12,display:'flex',justifyContent:'space-between',fontSize:11,color:'#666'}}>
        <span>TYCHE ABODES PRIVATE LIMITED — Confidential</span>
        <span>Report: {reportNumber} | Generated: {new Date().toLocaleString('en-IN')}</span>
      </div>
    </div>
  );
}